import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Save,
  Eye,
  Smartphone,
  Monitor,
  Mail,
  Settings,
  Users,
  TrendingUp,
  Copy,
  ExternalLink,
  Plus,
  Edit3,
  Palette,
  Code,
  Database,
  Webhook
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const LeadCaptureBuilder = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('mobile');
  const [activeIntegrationType, setActiveIntegrationType] = useState('internal');
  
  // Lead capture configuration
  const [captureConfig, setCaptureConfig] = useState({
    page_title: 'Join Our Community',
    headline: 'Get Exclusive Updates!',
    description: 'Subscribe to receive the latest news and special offers.',
    success_message: 'Thank you for subscribing!',
    redirect_url: '',
    background_color: '#ffffff',
    accent_color: '#f59e0b',
    text_color: '#333333',
    button_text: 'Subscribe Now',
    form_fields: [
      { id: '1', name: 'first_name', label: 'First Name', type: 'text', required: true, enabled: true },
      { id: '2', name: 'email', label: 'Email Address', type: 'email', required: true, enabled: true }
    ],
    integration_type: 'internal', // internal, aweber, getresponse, gmail
    integration_config: {
      aweber_list_id: '',
      aweber_form_id: '',
      getresponse_api_key: '',
      getresponse_campaign_id: '',
      gmail_address: ''
    }
  });

  const [stats, setStats] = useState({
    total_submissions: 0,
    conversion_rate: 0,
    recent_leads: []
  });

  const integrationTypes = [
    {
      id: 'internal',
      name: 'Internal Storage',
      description: 'Store leads in your BBC Quick List back office',
      icon: Database,
      setup_required: false
    },
    {
      id: 'aweber',
      name: 'AWeber',
      description: 'Connect to your AWeber email list',
      icon: Mail,
      setup_required: true
    },
    {
      id: 'getresponse',
      name: 'GetResponse',
      description: 'Connect to your GetResponse campaign',
      icon: Mail,
      setup_required: true
    },
    {
      id: 'gmail',
      name: 'Gmail Integration',
      description: 'Send leads directly to your Gmail inbox',
      icon: Mail,
      setup_required: true
    }
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email Address' },
    { value: 'tel', label: 'Phone Number' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio Button' }
  ];

  useEffect(() => {
    loadExistingCapture();
    loadStats();
  }, []);

  const loadExistingCapture = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/lead-capture`);
      if (response.ok) {
        const data = await response.json();
        if (data.capture) {
          setCaptureConfig(data.capture);
          setActiveIntegrationType(data.capture.integration_type);
        }
      }
    } catch (error) {
      console.error('Error loading capture page:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/lead-capture/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleAddField = () => {
    const newField = {
      id: Date.now().toString(),
      name: 'new_field',
      label: 'New Field',
      type: 'text',
      required: false,
      enabled: true
    };
    
    setCaptureConfig(prev => ({
      ...prev,
      form_fields: [...prev.form_fields, newField]
    }));
  };

  const handleUpdateField = (fieldId, property, value) => {
    setCaptureConfig(prev => ({
      ...prev,
      form_fields: prev.form_fields.map(field =>
        field.id === fieldId ? { ...field, [property]: value } : field
      )
    }));
  };

  const handleRemoveField = (fieldId) => {
    setCaptureConfig(prev => ({
      ...prev,
      form_fields: prev.form_fields.filter(field => field.id !== fieldId)
    }));
  };

  const handleSaveCapture = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/lead-capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(captureConfig)
      });

      if (response.ok) {
        toast.success('Lead capture page saved successfully!');
      } else {
        toast.error('Failed to save capture page');
      }
    } catch (error) {
      console.error('Error saving capture page:', error);
      toast.error('Failed to save capture page');
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrationChange = (integrationType) => {
    setActiveIntegrationType(integrationType);
    setCaptureConfig(prev => ({
      ...prev,
      integration_type: integrationType
    }));
  };

  // Lead Capture Preview Component
  const CapturePreview = () => (
    <div className={`${previewMode === 'mobile' ? 'max-w-sm' : 'max-w-lg'} mx-auto border rounded-lg overflow-hidden shadow-lg`}>
      <div 
        className="p-6"
        style={{ 
          backgroundColor: captureConfig.background_color,
          color: captureConfig.text_color 
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">{captureConfig.headline}</h1>
          <p className="opacity-80">{captureConfig.description}</p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {captureConfig.form_fields
            .filter(field => field.enabled)
            .map((field) => (
            <div key={field.id} className="space-y-2">
              <label className="block text-sm font-medium">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {field.type === 'textarea' ? (
                <textarea
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  className="w-full p-3 border rounded-lg"
                  rows={3}
                />
              ) : field.type === 'select' ? (
                <select className="w-full p-3 border rounded-lg">
                  <option>Select {field.label}</option>
                </select>
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">{field.label}</span>
                </div>
              ) : (
                <input
                  type={field.type}
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  className="w-full p-3 border rounded-lg"
                />
              )}
            </div>
          ))}

          <button 
            className="w-full p-3 rounded-lg font-semibold transition-colors"
            style={{ 
              backgroundColor: captureConfig.accent_color,
              color: '#ffffff'
            }}
          >
            {captureConfig.button_text}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs opacity-60">
            Powered by BBC Quick List
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Lead Capture Builder</h1>
            <p className="text-gray-600 mt-1">
              Build forms to capture leads and grow your prospect list
            </p>
          </div>
          
          <div className="flex space-x-3">
            <Button variant="outline" className="flex items-center">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button 
              onClick={handleSaveCapture}
              disabled={loading}
              className="btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Page'}
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">{stats.total_submissions}</p>
                  <p className="text-gray-600 text-sm">Total Submissions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">{stats.conversion_rate}%</p>
                  <p className="text-gray-600 text-sm">Conversion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">{stats.recent_leads.length}</p>
                  <p className="text-gray-600 text-sm">Recent Leads</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor Panel */}
          <div className="space-y-6">
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="fields">Fields</TabsTrigger>
                <TabsTrigger value="design">Design</TabsTrigger>
                <TabsTrigger value="integration">Integration</TabsTrigger>
              </TabsList>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Page Content</CardTitle>
                    <CardDescription>
                      Set your headline, description and success message
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="page_title">Page Title</Label>
                      <Input
                        id="page_title"
                        value={captureConfig.page_title}
                        onChange={(e) => setCaptureConfig(prev => ({...prev, page_title: e.target.value}))}
                        placeholder="Page title for SEO"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="headline">Main Headline</Label>
                      <Input
                        id="headline"
                        value={captureConfig.headline}
                        onChange={(e) => setCaptureConfig(prev => ({...prev, headline: e.target.value}))}
                        placeholder="Your compelling headline"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={captureConfig.description}
                        onChange={(e) => setCaptureConfig(prev => ({...prev, description: e.target.value}))}
                        placeholder="Explain what visitors will get"
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="button_text">Button Text</Label>
                      <Input
                        id="button_text"
                        value={captureConfig.button_text}
                        onChange={(e) => setCaptureConfig(prev => ({...prev, button_text: e.target.value}))}
                        placeholder="Subscribe Now"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="success_message">Success Message</Label>
                      <Textarea
                        id="success_message"
                        value={captureConfig.success_message}
                        onChange={(e) => setCaptureConfig(prev => ({...prev, success_message: e.target.value}))}
                        placeholder="Thank you message after submission"
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Fields Tab */}
              <TabsContent value="fields" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Form Fields</CardTitle>
                      <CardDescription>
                        Add and customize form fields
                      </CardDescription>
                    </div>
                    <Button onClick={handleAddField} size="sm" className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Field
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {captureConfig.form_fields.map((field, index) => (
                      <div key={field.id} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Field {index + 1}</span>
                          <div className="flex space-x-2">
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="checkbox"
                                checked={field.enabled}
                                onChange={(e) => handleUpdateField(field.id, 'enabled', e.target.checked)}
                                className="rounded"
                              />
                              <span>Enabled</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => handleUpdateField(field.id, 'required', e.target.checked)}
                                className="rounded"
                              />
                              <span>Required</span>
                            </label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveField(field.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label>Label</Label>
                            <Input
                              value={field.label}
                              onChange={(e) => handleUpdateField(field.id, 'label', e.target.value)}
                              placeholder="Field label"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Field Name</Label>
                            <Input
                              value={field.name}
                              onChange={(e) => handleUpdateField(field.id, 'name', e.target.value)}
                              placeholder="field_name"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Type</Label>
                            <select
                              value={field.type}
                              onChange={(e) => handleUpdateField(field.id, 'type', e.target.value)}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              {fieldTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Design Tab */}
              <TabsContent value="design" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Colors & Styling</CardTitle>
                    <CardDescription>
                      Customize the appearance of your form
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bg_color">Background</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="bg_color"
                            type="color"
                            value={captureConfig.background_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, background_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={captureConfig.background_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, background_color: e.target.value}))}
                            placeholder="#ffffff"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="text_color">Text Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="text_color"
                            type="color"
                            value={captureConfig.text_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, text_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={captureConfig.text_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, text_color: e.target.value}))}
                            placeholder="#333333"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accent_color">Button Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="accent_color"
                            type="color"
                            value={captureConfig.accent_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, accent_color: e.target.value}))}
                            className="w-12 h-10 p-1 border rounded"
                          />
                          <Input
                            value={captureConfig.accent_color}
                            onChange={(e) => setCaptureConfig(prev => ({...prev, accent_color: e.target.value}))}
                            placeholder="#f59e0b"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Integration Tab */}
              <TabsContent value="integration" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Integration</CardTitle>
                    <CardDescription>
                      Choose where to send your captured leads
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {integrationTypes.map((integration) => (
                        <div
                          key={integration.id}
                          onClick={() => handleIntegrationChange(integration.id)}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            activeIntegrationType === integration.id 
                              ? 'border-yellow-500 bg-yellow-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <integration.icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium">{integration.name}</h4>
                              <p className="text-sm text-gray-600">{integration.description}</p>
                              {integration.setup_required && (
                                <Badge variant="outline" className="mt-2">
                                  Setup Required
                                </Badge>
                              )}
                            </div>
                            {activeIntegrationType === integration.id && (
                              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Integration-specific settings */}
                    {activeIntegrationType === 'aweber' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">AWeber Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label>List ID</Label>
                            <Input
                              value={captureConfig.integration_config.aweber_list_id}
                              onChange={(e) => setCaptureConfig(prev => ({
                                ...prev,
                                integration_config: {
                                  ...prev.integration_config,
                                  aweber_list_id: e.target.value
                                }
                              }))}
                              placeholder="Your AWeber list ID"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Form ID</Label>
                            <Input
                              value={captureConfig.integration_config.aweber_form_id}
                              onChange={(e) => setCaptureConfig(prev => ({
                                ...prev,
                                integration_config: {
                                  ...prev.integration_config,
                                  aweber_form_id: e.target.value
                                }
                              }))}
                              placeholder="Your AWeber form ID"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {activeIntegrationType === 'getresponse' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">GetResponse Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label>API Key</Label>
                            <Input
                              type="password"
                              value={captureConfig.integration_config.getresponse_api_key}
                              onChange={(e) => setCaptureConfig(prev => ({
                                ...prev,
                                integration_config: {
                                  ...prev.integration_config,
                                  getresponse_api_key: e.target.value
                                }
                              }))}
                              placeholder="Your GetResponse API key"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Campaign ID</Label>
                            <Input
                              value={captureConfig.integration_config.getresponse_campaign_id}
                              onChange={(e) => setCaptureConfig(prev => ({
                                ...prev,
                                integration_config: {
                                  ...prev.integration_config,
                                  getresponse_campaign_id: e.target.value
                                }
                              }))}
                              placeholder="Your campaign ID"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {activeIntegrationType === 'gmail' && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Gmail Integration</CardTitle>
                          <CardDescription>
                            Leads will be sent as emails to your Gmail address
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label>Your Gmail Address</Label>
                            <Input
                              type="email"
                              value={captureConfig.integration_config.gmail_address}
                              onChange={(e) => setCaptureConfig(prev => ({
                                ...prev,
                                integration_config: {
                                  ...prev.integration_config,
                                  gmail_address: e.target.value
                                }
                              }))}
                              placeholder="your@gmail.com"
                            />
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Note:</strong> Each form submission will be sent as a formatted email to this address with all captured lead information.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Live Preview</CardTitle>
                  <div className="flex space-x-2">
                    <Button
                      variant={previewMode === 'mobile' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewMode('mobile')}
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={previewMode === 'desktop' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewMode('desktop')}
                    >
                      <Monitor className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CapturePreview />
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Your Page URL:</strong> bbcquicklist.com/capture/{user?.id}
                  </p>
                  <div className="flex space-x-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`bbcquicklist.com/capture/${user?.id}`);
                        toast.success('Link copied to clipboard!');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Link
                    </Button>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureBuilder;